import { useEffect, useRef, useState } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import HeaderFooterLayout from '../components/layouts/HeaderFooterLayout';
import Card from '../components/cards/Card';
import Button from '../components/Button';
import TextInput from '../components/TextInput';
import Select from '../components/Select';
import { ReactComponent as FilterIcon } from '../assets/images/icons/ic-filter.svg';
import Dialog from '../components/dialogs/Dialog';
import Table from '../components/Table';
import Pagination from '../components/Pagination';
import EmptyContent from '../components/EmptyContent';
import api from '../api';
import styles from './Mailings.module.css';
import detailStyles from './Forms.module.css';

const dateLabel = value => {
  if (!value) return '—';
  const date = new Date(/(?:Z|[+-]\d{2}:?\d{2})$/i.test(value) ? value : `${value}Z`);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('it-IT', { timeZone: 'Europe/Rome' });
};
const labels = { name: 'Nome', surname: 'Cognome', email: 'Email', company: 'Azienda', phone: 'Telefono', type: 'Tipo', topic: 'Topic', topics: 'Topic', privacy: 'Privacy', marketing: 'Marketing', source: 'Provenienza', page: 'Pagina', privacy_accepted_at: 'Data consenso privacy', privacy_policy_url: 'Informativa privacy', user: 'Dati persona', employees: 'Dipendenti' };

function FormValue({ value }) {
  if (value == null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Sì' : 'No';
  if (Array.isArray(value)) return value.length ? <ul>{value.map((item, index) => <li key={index}><FormValue value={item} /></li>)}</ul> : '—';
  if (typeof value === 'object') return <dl className={detailStyles.fields}>{Object.entries(value).map(([key, item]) => <div key={key}><dt>{labels[key] || key}</dt><dd><FormValue value={item} /></dd></div>)}</dl>;
  return String(value);
}

export default function Forms() {
  const [filters, setFilters] = useState({ q: '', type: '*', topic: '*' });
  const [filterOpen, setFilterOpen] = useState(false);
  const [options, setOptions] = useState({ types: [], topics: [] });
  const [optionError, setOptionError] = useState(false);
  const [page, setPage] = useState(0);
  const [result, setResult] = useState({ forms: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [reload, setReload] = useState(0);
  const requestRef = useRef(0);
  const per = 25;

  useEffect(() => {
    let disposed = false;
    setOptionError(false);
    api.get('/admin/forms/filters').then(data => {
      if (!disposed) setOptions(data);
    }).catch(() => { if (!disposed) setOptionError(true); });
    return () => { disposed = true; };
  }, [reload]);

  useEffect(() => {
    const request = ++requestRef.current;
    setLoading(true); setError('');
    const timer = setTimeout(async () => {
      const params = new URLSearchParams({ page, per });
      if (filters.q.trim()) params.set('q', filters.q.trim());
      if (filters.type !== '*') params.set('type', filters.type);
      if (filters.topic !== '*') params.set('topic', filters.topic);
      try {
        const data = await api.get(`/admin/forms?${params}`);
        if (request === requestRef.current) setResult(data);
      } catch {
        if (request === requestRef.current) { setError('Impossibile caricare le compilazioni. Riprova.'); setResult({ forms: [], count: 0 }); }
      } finally {
        if (request === requestRef.current) setLoading(false);
      }
    }, 250);
    return () => { clearTimeout(timer); requestRef.current += 1; };
  }, [filters, page, reload]);

  const changeFilter = (key, value) => { setPage(0); setFilters(current => ({ ...current, [key]: value })); };
  const typeOptions = [{ id: '*', value: '*', name: 'Tutti i tipi' }, ...options.types.map(value => ({ id: `type-${value}`, value, name: value || 'Senza tipo' }))];
  const topicOptions = [{ id: '*', value: '*', name: 'Tutti i topic' }, ...options.topics.map(value => ({ id: `topic-${value}`, value, name: value }))];
  const activeFilterCount = Number(filters.type !== '*') + Number(filters.topic !== '*');
  const rows = result.forms.map(form => ({
    ...form,
    person: { value: `${form.name} ${form.surname}`, component: <div className={styles.recipient}><strong>{`${form.name || ''} ${form.surname || ''}`.trim() || 'Nome non indicato'}</strong><span>{form.email || 'Email non indicata'}</span></div> },
    company: form.company || '—',
    type: form.type || 'Senza tipo',
    topics: { value: form.topics.join(', '), component: <span>{form.topics.join(', ') || '—'}</span> },
    submitted: { value: form.created_at, component: <span className={styles.date}>{dateLabel(form.created_at)}</span> },
  }));

  return <HeaderFooterLayout>
    <HelmetProvider><Helmet><title>Forms</title></Helmet></HelmetProvider>
    <div className={styles.container}><div className={styles.section}><div className={styles.sectionInner}>
      <header className={styles.header}><h1>Forms</h1><p>Consulta chi ha compilato i moduli e tutti i dati inviati.</p></header>
      <Card appearance="modern" integratedTable title="Compilazioni" actions={<span className={styles.resultCount} role="status">{loading ? 'Caricamento…' : `${result.count} compilazioni`}</span>}>
        <div className={styles.search}>
          <TextInput appearance="modern" type="search" value={filters.q} placeholder="Nome, email, azienda o telefono" onKeyUp={value => changeFilter('q', value)} />
          <Button appearance="subtle" additionalClass={styles.filterButton} aria-expanded={filterOpen} aria-controls="forms-filters" onClick={() => setFilterOpen(open => !open)}>
            <FilterIcon aria-hidden="true" /> Filtri{activeFilterCount ? ` (${activeFilterCount})` : ''}
          </Button>
          <Button appearance="subtle" onClick={() => setReload(value => value + 1)}>Aggiorna</Button>
        </div>
        <div id="forms-filters" className={styles.filters} hidden={!filterOpen}>
          {filterOpen && <>
            <div className={styles.filterField} role="group" aria-label="Tipo"><span>Tipo</span>
              <Select appearance="modern" portal placeholder="Tutti i tipi" options={typeOptions} selected={typeOptions.find(option => option.value === filters.type)} onSelect={option => changeFilter('type', option.value)} />
            </div>
            <div className={styles.filterField} role="group" aria-label="Topic"><span>Topic</span>
              <Select appearance="modern" portal placeholder="Tutti i topic" options={topicOptions} selected={topicOptions.find(option => option.value === filters.topic)} onSelect={option => changeFilter('topic', option.value)} />
            </div>
            <Button appearance="subtle" onClick={() => { setPage(0); setFilters({ q: '', type: '*', topic: '*' }); }}>Azzera filtri</Button>
          </>}
        </div>
        {optionError && <p role="alert" className={styles.error}>Impossibile caricare i filtri. Premi Aggiorna per riprovare.</p>}
        {error && <p role="alert" className={styles.error}>{error}</p>}
        <Table appearance="minimal" loading={loading} loadingN={8}
          style={{ border: 0, borderRadius: 0, boxShadow: 'none', marginTop: 0 }}
          onRowClick={row => setSelected(result.forms.find(form => form.id === row.id) || null)}
          columns={[
          { key: 'person', label: 'Persona', type: 'custom', wrap: true },
          { key: 'company', label: 'Azienda', type: 'string', wrap: true },
          { key: 'type', label: 'Tipo', type: 'string', wrap: true },
          { key: 'topics', label: 'Topic', type: 'custom', wrap: true },
          { key: 'submitted', label: 'Data compilazione', type: 'custom' },
        ]} elements={rows} />
        {!loading && !error && !result.forms.length && <EmptyContent title="Nessuna compilazione trovata" message="Prova a modificare i filtri selezionati." />}
        <div className={styles.pagination}><Pagination total={result.count} per={per} page={page} onChange={setPage} loading={loading} /></div>
      </Card>
    </div></div></div>
    <Dialog appearance="modern" open={!!selected} title={`Compilazione #${selected?.id || ''}`} onClose={() => setSelected(null)} style={{ width: '90vw', maxWidth: '800px' }} contentMaxHeight="75vh" overflow="scroll">
      {selected && <div className={detailStyles.detail}>
        <p>{dateLabel(selected.created_at)}</p>
        <FormValue value={selected.content} />
      </div>}
    </Dialog>
  </HeaderFooterLayout>;
}
